/**
 * Alpine.js data component for ChatKit panel
 * Handles panel state and communicates with Stimulus controller
 * Uses validated persistence with sessionStorage to maintain state across Turbo navigations
 * (sessionStorage = tab-specific, unlike localStorage which is shared across tabs)
 */
export function initChatkitPanel() {
  if (typeof globalThis.Alpine === "undefined") {
    console.warn("[ChatKit] Alpine.js is not available");
    return;
  }

  globalThis.Alpine.data("chatkitPanel", () => ({
    /**
     * Validates and sanitizes the open value from sessionStorage
     * Ensures only boolean values are accepted to prevent XSS
     */
    open: (() => {
      const storageKey = "chatkitPanel.open";
      const defaultValue = false;

      try {
        const storedValue = sessionStorage.getItem(storageKey);

        // If no value stored, return default
        if (storedValue === null) {
          return defaultValue;
        }

        // Parse the stored value
        const parsed = JSON.parse(storedValue);

        // Only accept boolean values
        if (typeof parsed === "boolean") {
          return parsed;
        }

        // If invalid type, remove corrupted value and return default
        console.warn(
          "[ChatKit] Invalid open value in sessionStorage, resetting to default",
        );
        sessionStorage.removeItem(storageKey);
        return defaultValue;
      } catch {
        // If parsing fails, remove corrupted value and return default
        console.warn(
          "[ChatKit] Failed to parse open from sessionStorage, resetting to default",
        );
        sessionStorage.removeItem(storageKey);
        return defaultValue;
      }
    })(),
    panelWidth: 448,

    init() {
      // Set initial data attribute based on persisted state
      this.$el.dataset.chatkitOpen = this.open;

      // Dispatch initial state change event if panel is open
      // This ensures React component mounts if panel was persisted as open
      if (this.open) {
        globalThis.dispatchEvent(
          new CustomEvent("chatkit-panel-state-change", {
            detail: { open: true },
          }),
        );
      }

      // Listen for toggle events
      globalThis.addEventListener("toggle-chatkit", () => {
        this.open = !this.open;
      });

      // Watch for state changes and update data attribute
      this.$watch("open", (value) => {
        this.$el.dataset.chatkitOpen = value;
        globalThis.dispatchEvent(
          new CustomEvent("chatkit-panel-state-change", {
            detail: { open: value },
          }),
        );

        // Persist to sessionStorage with validation
        if (typeof value === "boolean") {
          try {
            sessionStorage.setItem("chatkitPanel.open", JSON.stringify(value));
          } catch (error) {
            console.warn(
              `[ChatKit] Failed to persist open to sessionStorage: ${error.message}`,
            );
          }
        }
      });
    },
  }));
}
